/**
 * Calendar colour resolution.
 *
 * Two things can colour a calendar card:
 *
 * 1. The work order state, from STATUS_CONFIG, optionally overridden per organization in
 *    Asetukset > Ominaisuudet > Kalenteri (Organization.calendar_state_colors).
 * 2. A colour tag (väritunniste) attached to the work order. Each tag decides how it is drawn:
 *    'fill' paints the whole card and leaves the state as the card's left bar, 'border' keeps
 *    the state colours and rings the card in the tag colour.
 *
 * Only the accent colour is stored for an override; the light background and the readable text
 * colour are derived from it here, so a workshop picks one colour per state rather than three.
 */
import { darken, getContrastRatio, lighten } from '@mui/material/styles';

import { STATUS_CONFIG } from './calendarConstants';

/** Work order states drawn on the calendar, in legend order. Mirrors the backend's
 *  organizations.models.CALENDAR_STATE_COLOR_KEYS ("cancelled" is never drawn). */
export const CALENDAR_STATE_KEYS = [
  'quote',
  'planned',
  'in_progress',
  'completed',
  'picked_up',
  'waiting',
  'vehicle_in_workshop',
];

export const TAG_MODE_FILL = 'fill';
export const TAG_MODE_BORDER = 'border';

/** Highlight (calendar search) beats every other colour, exactly as before colour tags. */
export const HIGHLIGHT_COLORS = {
  bg: 'rgba(237, 137, 54, 0.3)',
  border: '#ed8936',
  text: '#744210',
};

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export function isHexColor(value) {
  return typeof value === 'string' && HEX_RE.test(value);
}

/**
 * Light background and dark text for an accent colour, so an override only needs one colour.
 * The built-in states keep their hand-tuned triples; this is used for overrides and tags.
 */
export function deriveStateColors(hex) {
  const bg = lighten(hex, 0.82);
  let text = darken(hex, 0.6);
  if (getContrastRatio(text, bg) < 4.5) {
    text = '#1f2937';
  }
  return { color: hex, bg, text };
}

/** White on dark fills, near-black on light ones. Deterministic, so E2E can assert it. */
export function deriveTagTextColor(hex) {
  if (!isHexColor(hex)) return undefined;
  return getContrastRatio('#ffffff', hex) >= getContrastRatio('#111827', hex) ? '#ffffff' : '#111827';
}

/**
 * State colours for one state, with the organization's override applied when it has one.
 * An unknown state falls back to planned, as every consumer did before.
 */
export function getStateConfig(state, overrides) {
  const base = STATUS_CONFIG[state] || STATUS_CONFIG.planned;
  const override = overrides && overrides[state];
  if (!isHexColor(override)) return base;
  return { ...base, ...deriveStateColors(override) };
}

/**
 * Every colour one card needs.
 *
 * `ringColor` is set only for a 'border' tag and is drawn as an outline around the card, so it
 * never competes with the state's left bar.
 */
export function resolveEventColors({ state, colorTagId, overrides, tagsById, highlighted }) {
  const stateConfig = getStateConfig(state, overrides);
  const tag = colorTagId != null && tagsById ? tagsById[colorTagId] : null;
  const tagIsFill = !!tag && isHexColor(tag.color) && tag.display_mode !== TAG_MODE_BORDER;
  const tagIsBorder = !!tag && isHexColor(tag.color) && tag.display_mode === TAG_MODE_BORDER;

  let bg = stateConfig.bg;
  let text = stateConfig.text;
  if (tagIsFill) {
    bg = tag.color;
    text = deriveTagTextColor(tag.color);
  }
  if (highlighted) {
    bg = HIGHLIGHT_COLORS.bg;
    text = HIGHLIGHT_COLORS.text;
  }

  return {
    bg,
    text,
    border: highlighted ? HIGHLIGHT_COLORS.border : stateConfig.color,
    // The ring survives highlighting: they are different channels and both carry meaning.
    ringColor: tagIsBorder ? tag.color : null,
    tag: tag || null,
    tagName: tag ? tag.name : null,
    tagColor: tag ? tag.color : null,
    tagMode: tag ? tag.display_mode || TAG_MODE_FILL : null,
    stateConfig,
  };
}
